import React, { useState, useEffect } from 'react';
import './Pages.css';

const FLEET_API_BASE = 'https://main.victriedemos.com:3017';
const FLEET_INTERMEDIARY_OID = 76040;
const FLEET_CLIENT_OID = 114074;
const FLEET_CLIENT_ID_NUMBER = '1100111';
const FLEET_BRANCH_OID = 462;
const FLEET_PAYMENT_INTERVAL = 4; // Monthly

const TABS = [
  { id: 'new', label: 'New Fleet', status: 0 },
  { id: 'pending', label: 'Pending Approval', status: 2 }
];

const pad = (n, len = 2) => String(n).padStart(len, '0');

// Backend expects "YYYY-MM-DD HH:mm:ss.SSS" for every datetime field (dateDone/start/end)
const formatDateTime = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
};

// Header matching is name-based/case-insensitive, not fixed-position (per FRONTEND_IMPLEMENTATION.TXT §4.2)
const CSV_FIELD_MATCHERS = {
  insuredPerson: (h) => h.includes('insured') || h.includes('person') || h.includes('name'),
  vehicle: (h) => h.includes('vehicle') || h.includes('reg') || h.includes('registration'),
  chassis: (h) => h.includes('chassis') || h.includes('vin') || h.includes('frame'),
  start: (h) => h === 'start',
  end: (h) => h === 'end',
  net: (h) => h.includes('net') || h.includes('premium') || h.includes('amount')
};

function parseFleetCSV(text) {
  const lines = text.split('\n').map(l => l.replace(/\r$/, '')).filter(l => l.trim() !== '');
  if (lines.length === 0) {
    throw new Error('CSV file is empty');
  }

  const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
  const colIndex = {};
  Object.keys(CSV_FIELD_MATCHERS).forEach(field => {
    colIndex[field] = headers.findIndex(h => CSV_FIELD_MATCHERS[field](h));
  });

  const stripQuotes = (v) => {
    const t = v.trim();
    if (t.length >= 2 && ((t[0] === '"' && t[t.length - 1] === '"') || (t[0] === "'" && t[t.length - 1] === "'"))) {
      return t.slice(1, -1);
    }
    return t;
  };

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = lines[i].split(',').map(stripQuotes);
    const get = (field) => (colIndex[field] >= 0 ? (cells[colIndex[field]] || '') : '');
    const netRaw = get('net');
    const netParsed = parseFloat(netRaw);

    rows.push({
      insuredPerson: get('insuredPerson'),
      vehicle: get('vehicle'),
      chassis: get('chassis'),
      start: get('start'),
      end: get('end'),
      netRaw,
      net: isNaN(netParsed) ? 0 : netParsed
    });
  }

  return rows;
}

const Declarations = () => {
  const doneBy = 'Zakim Insurance Agency';

  // Page shell
  const [activeTab, setActiveTab] = useState('new');
  const [fleets, setFleets] = useState([]);
  const [loadingFleets, setLoadingFleets] = useState(true);
  const [fleetsError, setFleetsError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [entryCounts, setEntryCounts] = useState({});

  // Wizard visibility
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);

  // Reference data (loaded once, on first wizard open)
  const [products, setProducts] = useState([]);
  const [refDataLoaded, setRefDataLoaded] = useState(false);

  // Step 1 — Cover Details
  const [selectedProductOID, setSelectedProductOID] = useState('');
  const [productCategory, setProductCategory] = useState(null);
  const [loadingProductCategory, setLoadingProductCategory] = useState(false);
  const [selectedCoverOptionOID, setSelectedCoverOptionOID] = useState('');
  const [insuredSchedule, setInsuredSchedule] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [scheduleError, setScheduleError] = useState('');

  // Step 2 — CSV upload
  const [csvRows, setCsvRows] = useState([]);
  const [csvFileName, setCsvFileName] = useState('');
  const [csvError, setCsvError] = useState('');
  const [csvParsing, setCsvParsing] = useState(false);

  // Step 3 — Review & submit
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fleetNo, setFleetNo] = useState(null);
  const [rowResults, setRowResults] = useState([]);
  const [submitError, setSubmitError] = useState('');
  const [submitComplete, setSubmitComplete] = useState(false);

  // Fleet detail view
  const [viewingFleet, setViewingFleet] = useState(null);
  const [fleetEntries, setFleetEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [entriesError, setEntriesError] = useState('');
  const [entrySearch, setEntrySearch] = useState('');


  useEffect(() => {
    fetchFleets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchTerm('');
    fetchFleets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchFleets = async () => {
    setLoadingFleets(true);
    setFleetsError('');
    try {
      const url = activeTab === 'pending'
        ? `${FLEET_API_BASE}/api/staff/getPendingIntermediaryFleet?agentCode=${FLEET_INTERMEDIARY_OID}`
        : `${FLEET_API_BASE}/api/staff/getMyFleets?username=${encodeURIComponent(doneBy)}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.success) {
        const list = data.data || [];
        setFleets(list);
        fetchEntryCounts(list);
      } else {
        setFleetsError(data.message || 'Failed to load fleets');
      }
    } catch (err) {
      console.error('Error fetching fleets:', err);
      setFleetsError('Unable to connect to fleet server');
    } finally {
      setLoadingFleets(false);
    }
  };

  // Fleet rows don't carry an entry count, so fetch each fleet's entries in parallel to derive one
  const fetchEntryCounts = (fleetList) => {
    fleetList.forEach((f) => {
      fetch(`${FLEET_API_BASE}/api/staff/getFleetEntries?fleetNo=${encodeURIComponent(f.FleetNo)}`)
        .then((res) => res.json())
        .then((data) => {
          setEntryCounts(prev => ({ ...prev, [f.FleetNo]: data.success ? (data.data || []).length : null }));
        })
        .catch((err) => {
          console.error('Error fetching entry count for', f.FleetNo, err);
          setEntryCounts(prev => ({ ...prev, [f.FleetNo]: null }));
        });
    });
  };

  const loadReferenceData = async () => {
    if (refDataLoaded) return;
    try {
      const res = await fetch(`${FLEET_API_BASE}/api/staff/getProducts`);
      const productsData = await res.json();
      const allProducts = productsData.success ? productsData.data : [];
      setProducts(allProducts.filter(p => /franchise business/i.test(p.product.ProductDescription || '')));
      setRefDataLoaded(true);
    } catch (err) {
      console.error('Error loading fleet reference data:', err);
      alert('Failed to load reference data from the fleet server');
    }
  };

  const resetWizard = () => {
    setWizardStep(1);
    setSelectedProductOID('');
    setProductCategory(null);
    setSelectedCoverOptionOID('');
    setInsuredSchedule(null);
    setScheduleError('');
    setCsvRows([]);
    setCsvFileName('');
    setCsvError('');
    setConfirmChecked(false);
    setFleetNo(null);
    setRowResults([]);
    setSubmitError('');
    setSubmitComplete(false);
  };

  const openWizard = () => {
    loadReferenceData();
    resetWizard();
    setShowWizard(true);
  };

  const closeWizard = () => {
    setShowWizard(false);
  };

  const finishWizard = () => {
    setShowWizard(false);
    setActiveTab('new');
    fetchFleets();
  };

  // ---- Step 1 ----

  const selectedProduct = products.find(p => String(p.product.OID) === String(selectedProductOID));

  const fetchSchedule = async (productOID, coverOptionOID) => {
    setLoadingSchedule(true);
    setScheduleError('');
    try {
      const res = await fetch(`${FLEET_API_BASE}/api/staff/getProductSchedule?product=${productOID}&coverOption=${coverOptionOID}`);
      const data = await res.json();
      if (data.success && data.data && data.data.length > 0) {
        setInsuredSchedule(data.data[0]);
      } else {
        setInsuredSchedule(null);
        setScheduleError('No insured schedule item is configured for this product/cover option combination');
      }
    } catch (err) {
      console.error('Error fetching product schedule:', err);
      setInsuredSchedule(null);
      setScheduleError('Failed to load insured schedule');
    } finally {
      setLoadingSchedule(false);
    }
  };

  const handleProductChange = async (oid) => {
    setSelectedProductOID(oid);
    setSelectedCoverOptionOID('');
    setInsuredSchedule(null);
    setScheduleError('');
    setProductCategory(null);

    if (!oid) return;

    setLoadingProductCategory(true);
    try {
      const res = await fetch(`${FLEET_API_BASE}/api/staff/getProductCategory?product=${oid}`);
      const data = await res.json();
      setProductCategory(data.RiskClassCategory ?? null);
    } catch (err) {
      console.error('Error fetching product category:', err);
      setProductCategory(null);
    } finally {
      setLoadingProductCategory(false);
    }
  };

  const handleCoverOptionChange = (oid) => {
    setSelectedCoverOptionOID(oid);
    setInsuredSchedule(null);
    setScheduleError('');
    if (oid) {
      fetchSchedule(selectedProductOID, oid);
    }
  };

  const step1Valid = !!(
    selectedProductOID && selectedCoverOptionOID && insuredSchedule
  );

  // ---- Step 2 ----

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setCsvError('Please upload a CSV file');
      return;
    }
    setCsvFileName(file.name);
    setCsvError('');
    setCsvParsing(true);

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        setCsvRows(parseFleetCSV(ev.target.result));
      } catch (err) {
        setCsvError('Error parsing CSV file: ' + err.message);
        setCsvRows([]);
      } finally {
        setCsvParsing(false);
      }
    };
    reader.onerror = () => {
      setCsvError('Error reading file');
      setCsvParsing(false);
    };
    reader.readAsText(file);
  };

  const clearCSV = () => {
    setCsvRows([]);
    setCsvFileName('');
    setCsvError('');
    const input = document.getElementById('fleet-csv-input');
    if (input) input.value = '';
  };

  const downloadSampleTemplate = () => {
    const a = document.createElement('a');
    a.href = '/fleet4.csv';
    a.download = 'fleet4.csv';
    a.click();
  };

  const csvTotal = csvRows.reduce((sum, r) => sum + (r.net || 0), 0);

  // ---- Step 3 ----

  const buildProposalPayload = (row, fleetNoValue) => ({
    VehicleReg: row.vehicle,
    start: formatDateTime(new Date(row.start)),
    end: formatDateTime(new Date(row.end)),
    customerName: row.insuredPerson,
    premium: String(row.net),
    chassisnumber: row.chassis,
    fleetNo: fleetNoValue,
    product: Number(selectedProductOID),
    coverOption: Number(selectedCoverOptionOID),
    insuredScheduleOID: insuredSchedule.OID,
    client: FLEET_CLIENT_OID,
    clientID: FLEET_CLIENT_ID_NUMBER,
    branch: FLEET_BRANCH_OID,
    intermediary: FLEET_INTERMEDIARY_OID,
    productCategory: productCategory,
    totalPremium: csvTotal,
    paymentInterval: FLEET_PAYMENT_INTERVAL,
    doneby: doneBy,
    dateDone: formatDateTime(new Date())
  });

  const submitRow = async (row, index, fleetNoValue) => {
    try {
      const res = await fetch(`${FLEET_API_BASE}/api/staff/createFleetProposal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildProposalPayload(row, fleetNoValue))
      });
      const data = await res.json();
      return {
        rowIndex: index,
        success: !!data.success,
        message: data.message || (data.success ? 'Created' : 'Failed'),
        narration: row.vehicle
      };
    } catch (err) {
      console.error('Error creating fleet proposal:', err);
      return { rowIndex: index, success: false, message: 'Network error', narration: row.vehicle };
    }
  };

  const handleSubmitFleet = async () => {
    if (!step1Valid || csvRows.length === 0 || !confirmChecked) return;

    setSubmitting(true);
    setSubmitError('');
    setRowResults([]);
    setSubmitComplete(false);

    let currentFleetNo = fleetNo;

    try {
      if (!currentFleetNo) {
        const res = await fetch(`${FLEET_API_BASE}/api/staff/createFleetPolicy`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            client: FLEET_CLIENT_OID,
            product: Number(selectedProductOID),
            coverOption: Number(selectedCoverOptionOID),
            branch: FLEET_BRANCH_OID,
            intermediary: FLEET_INTERMEDIARY_OID,
            insuredSchedule: insuredSchedule.OID,
            paymentInterval: FLEET_PAYMENT_INTERVAL,
            dateDone: formatDateTime(new Date()),
            doneby: doneBy,
            productCategory: productCategory,
            totalPremium: csvTotal
          })
        });
        const data = await res.json();
        if (!data.success || !data.fleetNo) {
          setSubmitError(data.message || 'Failed to create fleet');
          setSubmitting(false);
          return;
        }
        currentFleetNo = data.fleetNo;
        setFleetNo(currentFleetNo);
      }

      const results = [];
      for (let i = 0; i < csvRows.length; i++) {
        const result = await submitRow(csvRows[i], i, currentFleetNo);
        results.push(result);
        setRowResults([...results]);
      }

      setSubmitComplete(true);
      fetchFleets();
    } catch (err) {
      console.error('Error submitting fleet:', err);
      setSubmitError('An error occurred while submitting the fleet');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetryFailed = async () => {
    const failedIndices = rowResults.filter(r => !r.success).map(r => r.rowIndex);
    if (failedIndices.length === 0) return;

    setSubmitting(true);
    const results = [...rowResults];
    for (const idx of failedIndices) {
      const result = await submitRow(csvRows[idx], idx, fleetNo);
      const pos = results.findIndex(r => r.rowIndex === idx);
      results[pos] = result;
      setRowResults([...results]);
    }
    setSubmitting(false);
  };

  // ---- Fleet detail ----

  const openFleetEntries = async (fleet) => {
    setViewingFleet(fleet);
    setFleetEntries([]);
    setEntriesError('');
    setEntrySearch('');
    setLoadingEntries(true);
    try {
      const res = await fetch(`${FLEET_API_BASE}/api/staff/getFleetEntries?fleetNo=${encodeURIComponent(fleet.FleetNo)}`);
      const data = await res.json();
      if (data.success) {
        setFleetEntries(data.data || []);
      } else {
        setEntriesError(data.message || 'Failed to load fleet entries');
      }
    } catch (err) {
      console.error('Error fetching fleet entries:', err);
      setEntriesError('Unable to connect to fleet server');
    } finally {
      setLoadingEntries(false);
    }
  };

  const closeFleetEntries = () => {
    setViewingFleet(null);
    setFleetEntries([]);
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(amount || 0);
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const currentTabConfig = TABS.find(t => t.id === activeTab);
  const tabFleets = fleets.filter(f => f.Status === currentTabConfig.status);
  const filteredFleets = tabFleets.filter(f => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (f.FleetNo || '').toString().toLowerCase().includes(term) || (f.Dealer_name || '').toLowerCase().includes(term);
  });

  const filteredEntries = fleetEntries.filter(e => {
    if (!entrySearch) return true;
    const term = entrySearch.toLowerCase();
    return (e.Narration || '').toLowerCase().includes(term) || (e.CustomerName || '').toLowerCase().includes(term);
  });

  // ---- Fleet entries view ----

  if (viewingFleet) {
    return (
      <div className="page-container">
        <div className="page-header">
          <div>
            <h1>📄 Fleet Entries — {viewingFleet.FleetNo}</h1>
            <p className="page-subtitle">{viewingFleet.Dealer_name} · {viewingFleet.ProductDescription}</p>
          </div>
          <button className="back-to-policies-btn" onClick={closeFleetEntries}>← Back to Fleets</button>
        </div>

        <div className="search-bar">
          <input
            type="text"
            placeholder="🔍 Search by registration or customer name..."
            value={entrySearch}
            onChange={(e) => setEntrySearch(e.target.value)}
            className="search-input"
          />
        </div>

        {entriesError && <div className="error-banner">{entriesError}</div>}

        {loadingEntries ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading entries...</p>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="empty-state">
            <p>No entries found</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Registration</th>
                  <th>Customer Name</th>
                  <th>Product</th>
                  <th>Net Premium</th>
                  <th>Commencement</th>
                  <th>Expiry</th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.map((e, i) => (
                  <tr key={e.OID}>
                    <td>{i + 1}</td>
                    <td>{e.Narration}</td>
                    <td>{e.CustomerName}</td>
                    <td>{e.ProductDescription}</td>
                    <td className="amount">{formatCurrency(e.NetPremium)}</td>
                    <td>{formatDate(e.CommencementDate)}</td>
                    <td>{formatDate(e.ExpiryDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // ---- Create Fleet wizard ----

  if (showWizard) {
    return (
      <div className="page-container">
        <div className="page-header">
          <div>
            <h1>➕ Create New Fleet</h1>
            <p className="page-subtitle">Step {wizardStep} of 3</p>
          </div>
          <button className="back-to-policies-btn" onClick={closeWizard}>✕ Cancel</button>
        </div>

        <div className="form-container-narrow">
          <div className="step-content">

            {wizardStep === 1 && (
              <>
                <div className="step-header">
                  <h2>📋 Cover Details</h2>
                  <p className="step-description">Set the product and cover terms for this fleet</p>
                </div>

                <div className="form-grid">
                  <div className="form-group-icon">
                    <label>📦 Product *</label>
                    <select
                      value={selectedProductOID}
                      onChange={(e) => handleProductChange(e.target.value)}
                      className="form-input-narrow"
                    >
                      <option value="">-- Select Product --</option>
                      {products.map(p => (
                        <option key={p.product.OID} value={p.product.OID}>{p.product.ProductDescription}</option>
                      ))}
                    </select>
                    {loadingProductCategory && <small className="form-hint">Loading category...</small>}
                  </div>

                  {selectedProduct && (
                    <div className="form-group-icon">
                      <label>🛡️ Cover Option *</label>
                      <select
                        value={selectedCoverOptionOID}
                        onChange={(e) => handleCoverOptionChange(e.target.value)}
                        className="form-input-narrow"
                      >
                        <option value="">-- Select Cover Option --</option>
                        {selectedProduct.coverOptions.map(co => (
                          <option key={co.OID} value={co.OID}>{co.OptionName}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="form-group-icon">
                    <label>📄 Insured Schedule</label>
                    <input
                      type="text"
                      value={loadingSchedule ? 'Loading...' : (insuredSchedule ? insuredSchedule.ItemName : '')}
                      className="form-input-narrow disabled-input"
                      readOnly
                      disabled
                    />
                    {scheduleError && <small className="form-hint" style={{ color: '#e74c3c' }}>{scheduleError}</small>}
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={closeWizard}>✕ Cancel</button>
                  <button type="button" className="btn-primary" disabled={!step1Valid} onClick={() => setWizardStep(2)}>
                    Continue to CSV Upload →
                  </button>
                </div>
              </>
            )}

            {wizardStep === 2 && (
              <>
                <div className="step-header">
                  <h2>📁 Upload Vehicle Schedule</h2>
                  <p className="step-description">Upload a CSV with Insured Person, Vehicle, Chassis, Start, End, Net columns</p>
                </div>

                <div className="form-actions-center" style={{ gap: '10px', marginBottom: '20px' }}>
                  <label htmlFor="fleet-csv-input" className="primary-btn-narrow" style={{ cursor: 'pointer', display: 'inline-block' }}>
                    📤 Choose CSV File
                  </label>
                  <input id="fleet-csv-input" type="file" accept=".csv" onChange={handleCSVUpload} style={{ display: 'none' }} />
                  <button type="button" className="secondary-btn-narrow" onClick={downloadSampleTemplate}>📥 Download Sample Template</button>
                  {csvRows.length > 0 && (
                    <button type="button" className="action-button-narrow" onClick={clearCSV} style={{ backgroundColor: '#dc3545' }}>🗑️ Clear</button>
                  )}
                </div>

                {csvFileName && (
                  <div style={{ textAlign: 'center', marginBottom: '15px', color: '#28a745' }}>
                    ✅ File loaded: <strong>{csvFileName}</strong> ({csvRows.length} rows)
                  </div>
                )}

                {csvError && <div className="error-banner">{csvError}</div>}

                {csvRows.length > 0 && (
                  <>
                    <div className="table-container" style={{ maxHeight: 400, overflowY: 'auto' }}>
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Insured Person</th>
                            <th>Vehicle</th>
                            <th>Chassis</th>
                            <th>Start</th>
                            <th>End</th>
                            <th>Net</th>
                          </tr>
                        </thead>
                        <tbody>
                          {csvRows.map((r, i) => (
                            <tr key={i}>
                              <td>{i + 1}</td>
                              <td>{r.insuredPerson || '-'}</td>
                              <td>{r.vehicle || '-'}</td>
                              <td>{r.chassis || '-'}</td>
                              <td>{r.start || '-'}</td>
                              <td>{r.end || '-'}</td>
                              <td className="amount">{formatCurrency(r.net)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="summary-row highlight" style={{ marginTop: 15 }}>
                      <span>Total Net Premium:</span>
                      <strong className="amount-text">{formatCurrency(csvTotal)}</strong>
                    </div>
                  </>
                )}

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={() => setWizardStep(1)}>← Back to Cover Details</button>
                  <button type="button" className="btn-primary" disabled={csvRows.length === 0 || csvParsing} onClick={() => setWizardStep(3)}>
                    Continue to Review →
                  </button>
                </div>
              </>
            )}

            {wizardStep === 3 && (
              <>
                <div className="step-header">
                  <h2>✅ Review & Submit</h2>
                  <p className="step-description">Confirm the details below before submitting the fleet</p>
                </div>

                <div className="info-card highlight-card">
                  <div className="card-header"><h3>📋 Fleet Summary</h3></div>
                  <div className="info-grid">
                    <div className="info-item"><span className="info-label">Product</span><span className="info-value">{selectedProduct?.product.ProductDescription}</span></div>
                    <div className="info-item"><span className="info-label">Insured Schedule</span><span className="info-value">{insuredSchedule?.ItemName}</span></div>
                    <div className="info-item"><span className="info-label">Source File</span><span className="info-value">{csvFileName}</span></div>
                    <div className="info-item"><span className="info-label">Vehicles</span><span className="info-value">{csvRows.length}</span></div>
                    <div className="info-item"><span className="info-label">Total Premium</span><span className="info-value bold amount-text">{formatCurrency(csvTotal)}</span></div>
                  </div>
                </div>

                {!submitting && !submitComplete && (
                  <div className="form-group-icon" style={{ marginTop: 20 }}>
                    <label>
                      <input type="checkbox" checked={confirmChecked} onChange={(e) => setConfirmChecked(e.target.checked)} style={{ marginRight: 8 }} />
                      I confirm this is correct
                    </label>
                  </div>
                )}

                {submitError && <div className="error-banner">{submitError}</div>}

                {(submitting || rowResults.length > 0) && (
                  <div className="info-card highlight-card" style={{ marginTop: 20 }}>
                    <div className="card-header"><h3>{submitting ? '⏳ Submitting...' : '📊 Results'}</h3></div>
                    <p>{rowResults.length} of {csvRows.length} vehicles processed</p>
                    <ul style={{ maxHeight: 250, overflowY: 'auto' }}>
                      {rowResults.map((r) => (
                        <li key={r.rowIndex}>{r.success ? '✅' : '❌'} {r.narration} — {r.message}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="form-actions">
                  {!submitComplete ? (
                    <>
                      <button type="button" className="btn-secondary" onClick={() => setWizardStep(2)} disabled={submitting}>← Back to CSV</button>
                      <button type="button" className="btn-primary btn-success" disabled={submitting || !confirmChecked} onClick={handleSubmitFleet}>
                        {submitting ? '⏳ Submitting Fleet...' : '✓ Submit Fleet'}
                      </button>
                    </>
                  ) : (
                    <>
                      {rowResults.some(r => !r.success) && (
                        <button type="button" className="btn-secondary" onClick={handleRetryFailed} disabled={submitting}>
                          🔁 Retry Failed Rows
                        </button>
                      )}
                      <button type="button" className="btn-primary btn-success" onClick={finishWizard}>
                        ✓ Done
                      </button>
                    </>
                  )}
                </div>
              </>
            )}

          </div>
        </div>
      </div>
    );
  }

  // ---- Main shell: tabs + fleet list ----

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>🚛 Fleet Declarations</h1>
          <p className="page-subtitle">Create and manage fleet policy declarations</p>
        </div>
        <button className="primary-btn-narrow" onClick={openWizard}>➕ Create New Fleet</button>
      </div>

      <div className="tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="🔍 Search by Fleet No..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {fleetsError && <div className="error-banner">{fleetsError}</div>}

      {loadingFleets ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading fleets...</p>
        </div>
      ) : filteredFleets.length === 0 ? (
        <div className="empty-state">
          <p>No fleets in this category</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Fleet No</th>
                <th>Product</th>
                <th>Entries</th>
                <th>Premium</th>
                <th>Date Done</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFleets.map(f => (
                <tr key={f.OID}>
                  <td className="policy-number">{f.FleetNo}</td>
                  <td>{f.ProductDescription || f.Product}</td>
                  <td style={{ textAlign: 'center' }}>{entryCounts[f.FleetNo] != null ? entryCounts[f.FleetNo] : '…'}</td>
                  <td className="amount">{formatCurrency(f.FleetPremium)}</td>
                  <td>{formatDate(f.DateDone)}</td>
                  <td>
                    <span className={`status-badge ${f.Status === 0 ? 'under-review' : f.Status === 2 ? 'pending' : 'approved'}`}>
                      {f.Status === 0 ? 'New' : f.Status === 2 ? 'Pending Approval' : 'Approved'}
                    </span>
                  </td>
                  <td>
                    <div className="row-actions">
                      <button type="button" className="btn btn-small btn-view" onClick={() => openFleetEntries(f)}>👁️ View</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
};

export default Declarations;
